import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/components/RemoteTile.tsx
// Plays an externally-hosted media URL, clock-synced to the server.
//
// The <video> streams straight from the third-party origin — GridChat's server
// is never in the media path. All it supplies is the URL and the clock anchor.
//
// Handles both progressive files (MP4/WebM) and HLS. HLS matters more than it
// sounds: TTN's bumpers are all `type: "hls"` off b.watchttn.com, and a plain
// <video src="....m3u8"> plays only in Safari. Chrome and Firefox have no
// native HLS at all, so without the hls.js path below the entire URL half of
// the mirror shows an error for most of the room.
//
//   npm i hls.js
//
// Render from VideoGrid alongside YouTubeTile / StreamTile when
// currentRemoteVideo is non-null.
import React, { useEffect, useRef, useState, useCallback } from 'react';
/** Below this drift we leave playback alone. Seeking costs a rebuffer. */
const SOFT_DRIFT_SEC = 2.0;
/** Above this we hard-seek instead of nudging playbackRate. */
const HARD_DRIFT_SEC = 6.0;
/** Gentle catch-up rate when drift is between soft and hard. */
const NUDGE_RATE = 1.04;
/** How often we compare local position against the server clock. */
const RECONCILE_MS = 2000;
const HLS_MIME = 'application/vnd.apple.mpegurl';
/**
 * Trust the declared mime first — the server resolves it from TTN's own
 * `type: "hls"` field — and fall back to the extension. The extension check
 * alone is not enough: plenty of HLS manifests carry query strings, and some
 * carry no `.m3u8` at all.
 */
function isHlsSource(url, mimeType) {
    if (mimeType === HLS_MIME || mimeType === 'application/x-mpegURL')
        return true;
    return /\.m3u8(\?|#|$)/i.test(url);
}
/** Safari and iOS play HLS in the element itself; nothing else does. */
function hasNativeHls(el) {
    return el.canPlayType(HLS_MIME) !== '';
}
/**
 * Resolves templated variables (such as ${token}) if present.
 * If the URL has no templating, it passes through untouched without touching any network.
 */
async function resolveStreamVariables(rawUrl, requestOptions) {
    if (!rawUrl || !rawUrl.includes('${')) {
        return rawUrl;
    }
    let finalUrl = rawUrl;
    // Path 1: Resolving via explicit requestOptions metadata if supplied
    if (requestOptions?.variables?.length) {
        for (const v of requestOptions.variables) {
            const placeholder = `\${${v.key}}`;
            if (finalUrl.includes(placeholder) && v.source?.url) {
                try {
                    const res = await fetch(v.source.url, {
                        method: 'GET',
                        referrerPolicy: requestOptions.referrerPolicy || 'no-referrer',
                    });
                    if (res.ok) {
                        const token = (await res.text()).trim();
                        finalUrl = finalUrl.split(placeholder).join(token);
                    }
                    else {
                        console.warn(`[RemoteTile] Token fetch for ${v.key} returned HTTP ${res.status}`);
                    }
                }
                catch (err) {
                    console.error(`[RemoteTile] Failed to fetch client token for ${v.key}:`, err);
                }
            }
        }
    }
    // Path 2: Direct origin fallback if requestOptions was omitted in the socket frame
    if (finalUrl.includes('${token}')) {
        try {
            const origin = new URL(rawUrl).origin;
            const target = `${origin}/generate.php`;
            console.log(`[RemoteTile] Resolving ${target} directly from origin...`);
            const res = await fetch(target, {
                method: 'GET',
                referrerPolicy: 'no-referrer',
            });
            if (res.ok) {
                const token = (await res.text()).trim();
                finalUrl = finalUrl.split('${token}').join(token);
            }
            else {
                console.warn(`[RemoteTile] Fallback endpoint returned HTTP ${res.status}`);
            }
        }
        catch (err) {
            console.error('[RemoteTile] Fallback token request failed:', err);
        }
    }
    return finalUrl;
}
const RemoteTile = ({ socket, isHost, featuredMode }) => {
    const videoRef = useRef(null);
    const stateRef = useRef(null);
    /** serverNow - clientNow at receipt: converts server epoch to local epoch. */
    const clockOffsetRef = useRef(0);
    /** Live hls.js instance, so the reconcile loop and cleanup can reach it. */
    const hlsRef = useRef(null);
    const [current, setCurrent] = useState(null);
    const [muted, setMuted] = useState(true);
    const [stalled, setStalled] = useState(false);
    const [failed, setFailed] = useState(null);
    /** Where the server says we should be, right now, in media seconds. */
    const targetPosition = useCallback(() => {
        const s = stateRef.current;
        if (!s || s.startTime === null)
            return null;
        const serverNow = Date.now() + clockOffsetRef.current;
        const at = s.isPaused && s.pausedAt !== null ? s.pausedAt : serverNow;
        return Math.max(0, (at - s.startTime) / 1000);
    }, []);
    // ---- receive server state ------------------------------------------------
    useEffect(() => {
        if (!socket)
            return;
        const onUpdate = (update) => {
            clockOffsetRef.current = update.serverNow - Date.now();
            stateRef.current = update;
            setCurrent((prev) => {
                const next = update.currentVideo;
                if (!next)
                    return null;
                const mergedOptions = next.requestOptions || update.requestOptions;
                const updatedVideo = { ...next, requestOptions: mergedOptions };
                if (prev?.id === updatedVideo.id &&
                    prev?.url === updatedVideo.url &&
                    Boolean(prev?.requestOptions) === Boolean(updatedVideo.requestOptions)) {
                    return prev;
                }
                setFailed(null);
                setStalled(false);
                return updatedVideo;
            });
        };
        socket.on('remote-video-updated', onUpdate);
        socket.emit('get-remote-state');
        return () => {
            socket.off('remote-video-updated', onUpdate);
        };
    }, [socket]);
    // ---- attach the source ---------------------------------------------------
    useEffect(() => {
        const el = videoRef.current;
        if (!el || !current)
            return;
        let cancelled = false;
        let hls = null;
        const attach = async () => {
            const options = current.requestOptions || stateRef.current?.requestOptions;
            const playableUrl = await resolveStreamVariables(current.url, options);
            if (cancelled)
                return;
            if (playableUrl.includes('${')) {
                setFailed('Failed to authorize stream (token acquisition failed)');
                return;
            }
            if (!isHlsSource(playableUrl, current.mimeType) || hasNativeHls(el)) {
                el.src = playableUrl;
                return;
            }
            let Hls;
            try {
                Hls = (await import('hls.js')).default;
            }
            catch {
                setFailed('HLS playback needs hls.js (npm i hls.js)');
                return;
            }
            if (cancelled)
                return;
            if (!Hls.isSupported()) {
                setFailed('This browser cannot play HLS');
                return;
            }
            hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false,
                xhrSetup: (xhr) => {
                    xhr.withCredentials = false;
                },
            });
            hlsRef.current = hls;
            hls.on(Hls.Events.ERROR, (_evt, data) => {
                if (!data?.fatal)
                    return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                    hls.startLoad();
                }
                else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                    hls.recoverMediaError();
                }
                else {
                    hls.destroy();
                    hlsRef.current = null;
                    setFailed(`HLS playback failed (${data.details ?? 'unknown'})`);
                }
            });
            hls.loadSource(playableUrl);
            hls.attachMedia(el);
        };
        void attach();
        return () => {
            cancelled = true;
            if (hls) {
                try {
                    hls.destroy();
                }
                catch {
                    /* already torn down */
                }
            }
            hlsRef.current = null;
            el.removeAttribute('src');
            try {
                el.load();
            }
            catch {
                /* ignore */
            }
        };
    }, [current]);
    // ---- apply state to the element -----------------------------------------
    useEffect(() => {
        const reconcile = () => {
            const el = videoRef.current;
            const s = stateRef.current;
            if (!el || !s || !current)
                return;
            const target = targetPosition();
            if (target === null)
                return;
            if (el.duration && Number.isFinite(el.duration) && target >= el.duration)
                return;
            if (el.readyState < 1)
                return;
            if (s.isPaused) {
                if (!el.paused)
                    el.pause();
                if (Math.abs(el.currentTime - target) > 0.5)
                    el.currentTime = target;
                el.playbackRate = 1;
                return;
            }
            const drift = el.currentTime - target;
            if (Math.abs(drift) > HARD_DRIFT_SEC) {
                el.currentTime = target;
                el.playbackRate = 1;
            }
            else if (Math.abs(drift) > SOFT_DRIFT_SEC) {
                el.playbackRate = drift < 0 ? NUDGE_RATE : 1 / NUDGE_RATE;
            }
            else {
                el.playbackRate = 1;
            }
            if (el.paused && el.readyState >= 2) {
                el.play().catch(() => { });
            }
        };
        reconcile();
        const t = setInterval(reconcile, RECONCILE_MS);
        return () => clearInterval(t);
    }, [current, targetPosition]);
    // ---- element events ------------------------------------------------------
    const handleLoadedMetadata = useCallback(() => {
        const el = videoRef.current;
        const target = targetPosition();
        if (el && target !== null && Math.abs(el.currentTime - target) > 0.5) {
            el.currentTime = target;
        }
    }, [targetPosition]);
    const handleEnded = useCallback(() => {
        if (current)
            socket?.emit('remote-video-ended', { id: current.id });
    }, [socket, current]);
    const handleError = useCallback(() => {
        if (hlsRef.current)
            return;
        const err = videoRef.current?.error;
        const reasons = {
            1: 'Load aborted',
            2: 'Network error reaching the source',
            3: 'Cannot decode this file',
            4: 'Source unavailable or blocked (CORS / hotlink protection)',
        };
        setFailed(reasons[err?.code ?? 4] ?? 'Playback failed');
    }, []);
    const togglePlay = useCallback(() => {
        if (!isHost || !stateRef.current)
            return;
        socket?.emit('remote-control', {
            action: stateRef.current.isPaused ? 'play' : 'pause',
        });
    }, [isHost, socket]);
    if (!current)
        return null;
    return (_jsxs("div", { className: `remote-tile ${featuredMode ? 'featured' : ''}`, children: [_jsx("video", { ref: videoRef, autoPlay: true, muted: muted, playsInline: true, preload: "auto", referrerPolicy: "no-referrer", onLoadedMetadata: handleLoadedMetadata, onEnded: handleEnded, onError: handleError, onWaiting: () => setStalled(true), onPlaying: () => setStalled(false), onClick: togglePlay, style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    background: '#000',
                    cursor: isHost ? 'pointer' : 'default',
                } }, current.id), muted && (_jsx("button", { className: "remote-tile__unmute", onClick: () => {
                    setMuted(false);
                    videoRef.current?.play().catch(() => { });
                }, children: "\uD83D\uDD0A Unmute" })), stalled && !failed && (_jsx("div", { className: "remote-tile__status", children: "Buffering\u2026" })), failed && (_jsxs("div", { className: "remote-tile__status remote-tile__status--error", children: [failed, isHost && (_jsx("button", { onClick: () => socket?.emit('remote-control', { action: 'skip' }), children: "Skip" }))] })), _jsx("div", { className: "remote-tile__title", children: current.title })] }));
};
export default RemoteTile;
